import { useEffect, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import i18n from '../i18n';
import { logProductionEvent, PRODUCTION_EVENT_TYPES } from '../utils/productionEvents';

export const NOTE_CATEGORIES = [
  { key: 'material-delay',    labelKey: 'noteCategories.material-delay',    color: 'warning' },
  { key: 'equipment-issue',   labelKey: 'noteCategories.equipment-issue',   color: 'danger'  },
  { key: 'rework-required',   labelKey: 'noteCategories.rework-required',   color: 'warning' },
  { key: 'quality-concern',   labelKey: 'noteCategories.quality-concern',   color: 'danger'  },
  { key: 'staffing-issue',    labelKey: 'noteCategories.staffing-issue',    color: 'info'    },
  { key: 'waiting-approval',  labelKey: 'noteCategories.waiting-approval',  color: 'info'    },
  { key: 'setup-longer',      labelKey: 'noteCategories.setup-longer',      color: 'warning' },
  { key: 'product-changeover', labelKey: 'noteCategories.product-changeover', color: 'neutral' },
  { key: 'missing-info',      labelKey: 'noteCategories.missing-info',      color: 'warning' },
  { key: 'special-condition', labelKey: 'noteCategories.special-condition', color: 'neutral' },
  { key: 'general',           labelKey: 'noteCategories.general',           color: 'neutral' },
];

export const NOTE_CATEGORY_MAP = Object.fromEntries(NOTE_CATEGORIES.map((c) => [c.key, c]));

export function useTaskNotes(taskId, companyId) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taskId || !companyId) { setLoading(false); return; }

    const q = query(
      collection(db, COLLECTIONS.TASK_NOTES),
      where('companyId', '==', companyId),
      where('taskId', '==', taskId),
      where('archived', '==', false),
      orderBy('createdAt', 'asc'),
    );

    const unsub = onSnapshot(q, (snap) => {
      setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    return unsub;
  }, [taskId, companyId]);

  return { notes, loading };
}

export async function addTaskNote({
  companyId,
  taskId,
  taskCreatedBy,
  userId,
  userDisplayName,
  category,
  noteText,
  severity,
  task,
}) {
  const text = (noteText || '').trim().slice(0, 500);
  const noteRef = await addDoc(collection(db, COLLECTIONS.TASK_NOTES), {
    companyId,
    taskId,
    taskCreatedBy,
    userId,
    userDisplayName: userDisplayName || null,
    category,
    noteText: text || null,
    severity: severity || null,
    createdAt: serverTimestamp(),
    archived: false,
  });

  const eventType = category === 'material-delay'
    ? PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED
    : PRODUCTION_EVENT_TYPES.NOTE_ADDED;

  logProductionEvent({
    companyId,
    taskId,
    eventType,
    userId,
    task,
    notes: text || null,
    reason: i18n.t(NOTE_CATEGORY_MAP[category]?.labelKey || category, { defaultValue: category }),
    metadata: { noteCategory: category, severity: severity || null, noteId: noteRef.id },
  });
}
